/**
 * GoogleSignInButton — botón de SSO con Google Workspace (paquete AB).
 *
 * Comportamiento:
 *   - Si `VITE_GOOGLE_CLIENT_ID` está configurado: renderiza el botón
 *     oficial de Google (GIS) dentro de un container. Al click dispara
 *     el flow estándar (popup / One Tap) y devuelve un idToken.
 *   - Si no está configurado (mock/QA): renderiza un botón con la misma
 *     estética pero al click pide un email demo y arma un idToken FAKE
 *     para que el endpoint mock pueda decodificarlo. Tag visual "modo demo"
 *     para que sea evidente al revisor del MVP.
 *
 * El componente NO toca el session store directamente — recibe `onIdToken`
 * y delega. Así puede reutilizarse desde LoginPage (login normal) o desde
 * SecurityPage en una vista futura de "linkear cuenta Google a usuario".
 */

import { useEffect, useRef, useState } from 'react';
import { useGoogleSignIn } from './use-google-signin';

interface GoogleSignInButtonProps {
  /** Llamado con el idToken obtenido (real o mock). */
  onIdToken: (idToken: string) => void;
  /** Si true, el botón se ve deshabilitado (durante la mutation, etc.). */
  disabled?: boolean;
}

export function GoogleSignInButton({ onIdToken, disabled }: GoogleSignInButtonProps) {
  const { ready, error, modoMock, renderButton, signInMock } = useGoogleSignIn();
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef<boolean>(false);
  const [showMockPicker, setShowMockPicker] = useState(false);

  // Render del botón oficial cuando GIS está listo
  useEffect(() => {
    if (modoMock || !ready) return;
    if (renderedRef.current) return;
    const el = buttonContainerRef.current;
    if (!el) return;
    renderButton(el, onIdToken);
    renderedRef.current = true;
  }, [modoMock, ready, renderButton, onIdToken]);

  if (error) {
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
        Google SSO no disponible: {error}. Usá usuario/contraseña.
      </div>
    );
  }

  // Modo MOCK — botón propio que abre un picker de emails de QA
  if (modoMock) {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setShowMockPicker((v) => !v)}
          disabled={disabled}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
          aria-label="Continuar con Google Workspace (modo demo)"
        >
          <GoogleLogo />
          <span>Continuar con Google Workspace</span>
          <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-800">
            Demo
          </span>
        </button>

        {showMockPicker && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
            <p className="mb-2 text-[11px] uppercase tracking-wider text-gray-600">
              Elegí un email del Workspace mock
            </p>
            <div className="space-y-1">
              {MOCK_GOOGLE_USERS.map((u) => (
                <button
                  key={u.email}
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-white"
                  onClick={() => {
                    setShowMockPicker(false);
                    const idToken = signInMock(u.email, u.nombre);
                    onIdToken(idToken);
                  }}
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-everex-azul text-[11px] font-bold text-white">
                    {u.iniciales}
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">{u.nombre}</div>
                    <div className="font-mono text-[11px] text-gray-600">{u.email}</div>
                  </div>
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-gray-600">
              En producción: el flow real de Google sustituye este picker.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Modo REAL — container donde GIS renderiza el botón oficial
  return (
    <div className="space-y-1">
      <div ref={buttonContainerRef} className="flex justify-center" />
      {!ready && (
        <div className="text-center text-[11px] text-gray-600">
          Cargando Google Identity Services…
        </div>
      )}
    </div>
  );
}

/** SVG inline del logo "G" de Google — colores oficiales del brand guide. */
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}

/** Usuarios mock para el picker — sincronizado con SEED_USUARIOS.
 *
 * Laura (Pharmatrans) NO está acá porque su email no es @everexpharma.com
 * y el endpoint mock la rechazaría por dominio no autorizado. Eso refleja
 * la realidad: terceros sin Workspace deben usar usuario/password local. */
const MOCK_GOOGLE_USERS: ReadonlyArray<{ email: string; nombre: string; iniciales: string }> = [
  { email: 'christian.palazzo@everexpharma.com', nombre: 'Christian Palazzo', iniciales: 'CP' },
  { email: 'pablo.mendez@everexpharma.com', nombre: 'Pablo Méndez', iniciales: 'PM' },
  { email: 'martina.sosa@everexpharma.com', nombre: 'Martina Sosa', iniciales: 'MS' },
];
